import React, { Suspense, lazy, useCallback, useContext, useMemo, useState } from "react";
import { Archive, BellRing, Rocket, Trash2, Zap } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderRule from "./api";
import { loadRuleFilterDependencies } from "./features/rule.dependencies";
import { ruleFilterFields } from "./features/rule.filter";
import { getRuleColumns } from "./rule-list/ruleColumns";
import RuleRowActions from "./rule-list/RuleRowActions";
import RuleViewDrawer from "./rule-dialogs/RuleViewDrawer";

// The editor and the test run are only needed once opened.
const RuleEditor = lazy(() => import("./rule-editor/RuleEditor"));
const TestRunDialog = lazy(() => import("./rule-dialogs/TestRunDialog"));

// Reminder Designer -> Rule (tbl_rem_rule): a live reminder. Its trigger, schedule, audience, policies and ladder.
const Rule = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderRule.getAll, { loadError: "Failed to load rules.", initialSort: { key: "id", direction: "desc" } });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [editor, setEditor] = useState(null);
  const [view, setView] = useState(null);
  const [testRule, setTestRule] = useState(null);
  const [busy, setBusy] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Runs a row request with its busy spinner; `reload` refreshes the list afterwards. */
  const withBusy = async (row, action, run, { reload = false } = {}) => {
    setBusy({ id: row.id, action });
    try {
      await run();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
      if (reload) list.reload();
    }
  };

  const openView = (row) =>
    withBusy(row, "view", async () => {
      const [ruleRes, summaryRes] = await Promise.all([apiReminderRule.getById(row.id), apiReminderRule.getSummary(row.id)]);
      setView({ record: ruleRes.data?.data ?? null, summary: summaryRes.data?.data ?? null });
    });

  /** A lifecycle call whose success message comes from the server. */
  const lifecycle = (row, action, call) =>
    withBusy(
      row,
      action,
      async () => {
        const res = await call(row.id);
        showAlert({ type: "success", message: res.data?.message || "Done." });
      },
      { reload: true }
    );

  const handleActivate = async (row) => {
    if (row.published_version_no) {
      const confirmed = await openConfirm({
        title: "Activate again",
        message: `Publish the saved draft of "${row.name}" as version ${row.published_version_no + 1}? The rule runs the new version from its next run.`,
        confirmText: "Activate",
        variant: "primary",
        icon: <Rocket size={20} />,
      });
      if (!confirmed) return;
    }
    await withBusy(
      row,
      "activate",
      async () => {
        try {
          const res = await apiReminderRule.activate(row.id);
          showAlert({ type: "success", message: res.data?.message || "Activated." });
        } catch (err) {
          const problems = err.response?.data?.data?.problems ?? [];
          const more = problems.length > 1 ? ` (+${problems.length - 1} more; Test run lists them all)` : "";
          showAlert({ type: "error", title: "Cannot activate yet", message: `${errorMessage(err)}${more}` });
        }
      },
      { reload: true }
    );
  };

  const handleRunNow = async (row) => {
    const confirmed = await openConfirm({
      title: "Run Now",
      message: `Run "${row.name}" now, outside its schedule? It opens the cases it finds and really sends what is due. The schedule stays as it is.`,
      confirmText: "Run now",
      variant: "primary",
      icon: <Zap size={20} />,
    });
    if (confirmed) await lifecycle(row, "run", apiReminderRule.runNow);
  };

  const handleArchive = async (row) => {
    const confirmed = await openConfirm({
      title: "Archive Rule",
      message: `Archive "${row.name}"? It stops running and its open cases are stopped. An archived rule can't be activated again; copy it instead.`,
      confirmText: "Archive",
      variant: "warning",
      icon: <Archive size={20} />,
    });
    if (confirmed) await lifecycle(row, "archive", apiReminderRule.archive);
  };

  const deleteRules = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Rule",
      message: `Delete ${ids.length > 1 ? `${ids.length} rules` : "this rule"} and ${ids.length > 1 ? "their" : "its"} ladder? Only draft or archived rules without open cases can be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderRule.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const { reload } = list;
  const closeEditor = useCallback(
    (changed) => {
      setEditor(null);
      if (changed) reload();
    },
    [reload]
  );

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getRuleColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <RuleRowActions
      row={row}
      canManage={canManage}
      busy={busy}
      onView={openView}
      onEdit={(target) => setEditor({ id: target.id })}
      onTestRun={setTestRule}
      onRunNow={handleRunNow}
      onActivate={handleActivate}
      onPause={(target) => lifecycle(target, "pause", apiReminderRule.pause)}
      onResume={(target) => lifecycle(target, "resume", apiReminderRule.resume)}
      onClone={(target) => lifecycle(target, "clone", apiReminderRule.clone)}
      onArchive={handleArchive}
      onDelete={(target) => deleteRules([target.id])}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setEditor({ id: null })}
          onDeleteSelected={canManage ? () => deleteRules(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={ruleFilterFields} dependenciesLoader={loadRuleFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={BellRing}
              title={list.hasQuery ? "No rules match" : "No reminder rules yet"}
              message={
                list.hasQuery
                  ? "No rules match your search or filters. Try adjusting them."
                  : "A rule puts a trigger to work: when to check, whose cases count, and the ladder of reminders (who is told, on which channel, with which template)."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Create Rule"
              onCreate={() => setEditor({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <Suspense fallback={null}>
        {editor && <RuleEditor ruleId={editor.id} onClose={closeEditor} />}
        {testRule && <TestRunDialog rule={testRule} onClose={() => setTestRule(null)} />}
      </Suspense>

      {view && <RuleViewDrawer record={view.record} summary={view.summary} onClose={() => setView(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Rule;
